// Fixture composer — DECISION-024 AAA single-pipeline target.
//
// Reads per-kind manifest data, places catalogue geometry, runs slot-
// layout primitive, populates products, wires interactions + animations.
// Returns a composedEntry the stage hands onto for state.
//
// During the migration window, the composer COEXISTS with legacy per-
// kind anchor builders. Stage.js's fixture-build loop dispatches to
// EITHER the composer (kinds with a manifest in fixtureCatalogue) OR
// the legacy registered fixture (kinds without). When a kind's
// manifest authoring + cutover ship, its <kind>-anchor.js file
// deletes; the composer becomes its sole renderer. Phase 15 deletes
// `fixtures/` entirely.
//
// Honest scope note: this composer is currently scoped to BOX-FAMILY
// fixtures (longbox + shortbox). Decor kinds continue to use
// stage.js's `_renderCatalogueInstances` until Phase 15 absorbs that
// path as a degenerate-case branch here. Each per-file migration
// extends the composer to handle the next archetype's manifest shape
// (shelf, table, bin, spinner) — the dispatch is data-driven; the
// implementation is incremental.

import * as THREE from "../../../vendor/three.module.js";
import { boxedComicReferenceMeters } from "../../authoring/contracts.js";
import { resolveAnchorLabel } from "../fixtures/_label-helpers.js";
import { loadFixtureCatalogue } from "../_fixture-catalogue-loader.js";
import { loadFixtureManifest } from "./manifest-loader.js";
import { getSlotLayout } from "./slot-layouts/index.js";
import { socketLayoutToComposerSlotLayout, productsForSocket, socketsForAnchor } from "../_socket-transform.js";   // DECISION-052 socket→slot-layout + per-socket fill + world facts
import { isGrammarSocket, typesAccepting, typeRegistry } from "../../../../packages/layout-grammar/src/grammar.js";   // ASK-112 S4 — D-062 grammar sockets render from the socket, not the manifest lane
import { applyFixtureLibraryMaterials, applyHeaderSignage, collectMeshes } from "../_material-apply.js";   // DECISION-053 Slice-2: fixture material-library bindings + signage

// DECISION-051a — interaction kinds that OWN the fixture's geometry+product rendering, so
// the composer skips its Body-clone + slot-layout + populateComics (the interaction does it).
const GEOMETRY_OWNING_INTERACTIONS = new Set(["friction-spin"]);
import { getInteraction } from "./interactions/index.js";
import { getAnimation } from "./animations/index.js";

/**
 * Compose a fixture instance from manifest data.
 *
 * @param {Object} ctx
 * @param {Object} ctx.anchor           Anchor entry.
 * @param {Object} ctx.scene            Current scene config.
 * @param {Object} ctx.stage            Runtime Stage instance.
 * @param {Array}  ctx.products         Type-eligible product pool.
 * @param {Object} ctx.fixtureCatalogue boot.json.fixtureCatalogue.
 * @param {number} ctx.token            Stage compose token.
 * @returns {Promise<Object | null>}    composedEntry, or null on abort/no-manifest.
 */
/**
 * 🔴 ASK-159 §2.18gb ruling 7 (the kind half) — A NAMED NODE KEEPS ITS PLACE IN ITS OWN ASSET. `srcBody.clone(true)` copies
 * the node's LOCAL transform only, while the placement reference (`srcBbox`, `Box3.setFromObject`) is measured in the
 * asset's WORLD frame. Identical for every GLB whose Body/Lid sit at the root — and wrong by the parent's transform for
 * one whose DCC hierarchy nests them: `longbox-tall` (r66, pending row c3b10c54) parents Body + Lid under `ClosedBox` at
 * (1.192, −0.796, 0.5), so its body drew 1.4 m off its anchor and the lid's pivot (open-close takes the hinge's x/z from
 * the anchor) sat 1.4 m from the lid — measured locally under the override: L3 "the lid MOVES" 0.78–2.87 m, L4 0/16
 * sample points stationary. glTF node transforms compose down the hierarchy (glTF 2.0 §3.5.3), so the clone takes the
 * parent's world matrix: a root-level node is unchanged by construction.
 */
export function cloneInWorldFrame(src) {
	const c = src.clone(true);
	const parent = src.parent;
	if (parent) { parent.updateWorldMatrix(true, false); c.applyMatrix4(parent.matrixWorld); }
	return c;
}

export async function composeFixture({ anchor, scene, stage, products, fixtureCatalogue, token }) {
	if (!anchor?.fixtureKind || !stage || token !== stage.currentToken) return null;
	const kind = anchor.fixtureKind;

	const manifest = await loadFixtureManifest({ catalogue: fixtureCatalogue, kind });
	if (!manifest) return null;
	if (token !== stage.currentToken) return null;

	// v2 + v3 schema: manifest.slots is an ARRAY of slot definitions.
	// Most fixture kinds today use a single entry (longbox = one "lane");
	// future multi-zone fixtures extend the array.
	//
	// v3 introduces `displayVariants[]` per slot — merchant picks a
	// variant via Studio v2; each variant declares its own
	// layoutAlgorithm + count envelope. v2 manifests carry
	// layoutAlgorithm directly on the slot def (single-variant
	// equivalent). `resolveActiveVariant` normalises to a canonical
	// variant shape regardless of source schema version.
	const slotDefs = Array.isArray(manifest.slots) ? manifest.slots : [];
	const primarySlotDef = slotDefs[0] || null;

	// Merchant overrides arrive via the instance's `knobs.slotOverrides`
	// per the v3 spec (Studio v2 → /admin/shops/{shopId}/instances → Forge
	// merges at mint). anchor.extras already holds the instance's knobs
	// flattened from boot.json.
	const slotOverrides = anchor.extras?.slotOverrides
		|| anchor.extras?.knobs?.slotOverrides
		|| {};
	const primarySlotOverride = primarySlotDef ? slotOverrides[primarySlotDef.slotId] : null;
	const primaryVariant = resolveActiveVariant(primarySlotDef, primarySlotOverride);

	// Manifest-declared slot-layout (longbox/shelf families declare it on the display variant,
	// e.g. longbox "filled-spines" → browse-bin). Socket-driven multi-socket fixtures (pile-
	// table) declare NO manifest.slots → layoutAlgo is null here and the per-socket placement
	// block below handles them via the catalogue sockets[] (DECISION-052).
	const manifestLayoutAlgo = primaryVariant?.layoutAlgorithm || null;

	// ASK-112 S4 — a socket that AUTHORS the D-062 grammar outranks the manifest lane.
	//
	// The two describe the same placement, and they disagree: the longbox manifest declares a
	// 0.71 m lane holding 113, while the socket's spline domain is the box's MEASURED inner wall
	// (0.648 m) and its capacity is derived from what the items actually spend. The manifest
	// number is authored; the socket's is measured, so the socket wins and the manifest lane read
	// stops applying to this fixture (ASK-112 S2 flag 3). Legacy sockets — every one but the
	// longbox today — are S2 ANNOTATIONS of their old layout name, not authored grammar, and are
	// deliberately NOT routed here: rendering them through the grammar would re-cut a dozen
	// shipped fixtures in a single commit. Same `grammarSource: "authored"` seam GCG's resolver
	// enforces on (S3), for the same reason.
	// 🔴 ASK-137 §119 F-119c — EVERY grammar socket, not the first. A holder with a derived TOP and
	// derived SHELVES (F-119a: one `surface` socket per board) used to compose the top and silently
	// drop every shelf — measured on a boot rewritten at the boundary: `SHELF_01` in the catalogue,
	// two comics assigned to it, no region registered, nothing drawn. The first socket still leads
	// (its type, its manifest fallback); the rest are laid out beside it below.
	const grammarSockets = (fixtureCatalogue?.[kind]?.sockets || []).filter(isGrammarSocket);
	const grammarSocket = grammarSockets[0] || null;
	const layoutAlgo = grammarSocket ? "grammar" : manifestLayoutAlgo;

	// DECISION-051a — an interaction may OWN rendering (geometry + products), mirroring the
	// productsRendered pattern. When present (spinner's friction-spin), the composer skips its
	// Body-only clone + slot-layout + populateComics; the interaction instances the whole GLB
	// and seats products itself (the spinner's rack lives under SPIN_ROOT, not under Body).
	const geometryOwned = (manifest.interactions || []).some((i) => GEOMETRY_OWNING_INTERACTIONS.has(i?.kind));

	// Preflight: every primitive referenced by the manifest must be
	// registered before we touch the scene. If anything is missing the
	// composer bails to null BEFORE any side effects, so stage.js's
	// fixture-build loop falls through to the legacy per-kind builder
	// for this anchor. This keeps the migration safe — a kind that
	// hasn't yet had its primitives ported continues to render via
	// legacy, even if a manifest exists for it.
	if (!geometryOwned && layoutAlgo && !getSlotLayout(layoutAlgo)) {
		console.warn(`[composer] ${kind}: slot-layout '${layoutAlgo}' not yet registered; deferring to legacy renderer`);
		return null;
	}
	for (const interactionDef of (manifest.interactions || [])) {
		if (interactionDef?.kind && !getInteraction(interactionDef.kind)) {
			console.warn(`[composer] ${kind}: interaction '${interactionDef.kind}' not yet registered; deferring to legacy renderer`);
			return null;
		}
	}
	for (const animationDef of (manifest.animations || [])) {
		if (animationDef?.kind && !getAnimation(animationDef.kind)) {
			console.warn(`[composer] ${kind}: animation '${animationDef.kind}' not yet registered; deferring to legacy renderer`);
			return null;
		}
	}

	// ─────── Geometry placement ───────
	// Skipped when an interaction owns geometry (DECISION-051a, spinner) — friction-spin
	// instances the whole GLB itself (its rack lives under SPIN_ROOT, not under Body).
	// `offsetVec` is hoisted because the grammar slot-layout composes the socket's DOMAIN with the
	// same transform this block applies to the body — catalogue-local → world has to be one answer.
	let bodyMesh = null, lidMesh = null, srcBbox = null, offsetVec = null;
	if (!geometryOwned) {
	const gltf = await loadFixtureCatalogue({ catalogue: fixtureCatalogue, kind, gltfLoader: stage.gltfLoader });
	if (token !== stage.currentToken) return null;
	const src = gltf?.scene;
	if (!src) {
		console.warn(`[composer] ${kind}: no catalogue scene available; skipping anchor ${anchor.name}`);
		return null;
	}
	const bodyNodeName = manifest.geometry?.bodyNode || "Body";
	const lidNodeName  = manifest.geometry?.lidNode  || null;
	const srcBody = src.getObjectByName(bodyNodeName);
	if (!srcBody) {
		console.warn(`[composer] ${kind}: catalogue missing ${bodyNodeName} node; skipping anchor ${anchor.name}`);
		return null;
	}
	const srcLid = lidNodeName ? src.getObjectByName(lidNodeName) : null;

	// Bbox-bottom-centre placement (only reference policy implemented
	// today — DECISION-024 schema reserves "named-node" for future).
	// MUST use `.add(offsetVec)` not `.copy(offsetVec)` so the catalogue
	// node's authored translation is preserved (shortbox lesson 2026-05-27).
	srcBody.updateMatrixWorld(true);
	srcBbox = new THREE.Box3().setFromObject(srcBody);
	const cataRef = new THREE.Vector3(
		(srcBbox.min.x + srcBbox.max.x) * 0.5,
		srcBbox.min.y,
		(srcBbox.min.z + srcBbox.max.z) * 0.5,
	);
	offsetVec = new THREE.Vector3().subVectors(anchor.position, cataRef);

	bodyMesh = cloneInWorldFrame(srcBody);
	bodyMesh.name = `${kind}__cat__${anchor.name}__Body`;
	bodyMesh.position.add(offsetVec);
	(stage._shellRoot || stage.stageRoot).add(bodyMesh);

	if (srcLid) {
		lidMesh = cloneInWorldFrame(srcLid);
		lidMesh.name = `${kind}__cat__${anchor.name}__Lid`;
		lidMesh.position.add(offsetVec);
		(stage._shellRoot || stage.stageRoot).add(lidMesh);
	}

	// Hide any shell-baked equivalents during the migration window so
	// they don't render alongside the catalogue clones. Shell naming
	// conventions vary by kind family (box-family uses _Body / _Lid;
	// shelf-family uses _Board + optional _Bracket_L/_R for book-row).
	// The kebab-cased kind maps to Pascal-case in the shell. Once
	// Content removes per-shell-baked meshes from shell GLBs (DECISION-022
	// Phase 4), this hide step retires entirely.
	if (stage._shellRoot) {
		hideShellBakedForKind(stage._shellRoot, kind, anchor.index || 1);
	}
	}   // end if (!geometryOwned)

	// ─────── Orientation ───────
	// Two sources combined:
	// 1. facingDeg (manifest defaults / Storefront knob / anchor extras)
	//    — declares the CANONICAL face direction the catalogue Body
	//    is authored at. Box-family default = 180 (-X); shelf-family
	//    typically uses 90 (+Z) or absent.
	// 2. anchor.rotationY — the Blender empty's Y-axis rotation. Each
	//    per-anchor instance applies this rotation on top of the
	//    canonical, letting one catalogue mesh serve every wall
	//    orientation (a single wall-shelf GLB serves the west wall via
	//    rotY=π/2, the south wall via rotY=0, etc.).
	const orient    = scene?.template?.knobs?.boxOrientations?.[anchor.name] || {};
	const facingDeg = orient.facingDeg ?? anchor.extras?.facingDeg ?? manifest.defaults?.facingDeg ?? 180;
	const facingRad = facingDeg * (Math.PI / 180);
	// Compass-on-XZ-plane: 0=E(+X), 90=N(+Z), 180=W(-X), 270=S(-Z).
	const baseFrontDir = new THREE.Vector3(Math.cos(facingRad), 0, Math.sin(facingRad)).normalize();
	// Rotate the canonical front direction by anchor.rotationY so the
	// per-instance orientation is the source of truth. Slot-layout
	// primitives that consume `frontDir` (linear-spine-row) see the
	// rotated direction; primitives that derive their own from
	// anchor.rotationY directly (shelf-row) are consistent.
	const anchorYawQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, anchor.rotationY || 0, 0, "YXZ"));
	const frontDir = baseFrontDir.clone().applyQuaternion(anchorYawQuat);

	// ─────── Label ───────
	// Built at canonical (facingDeg=180) front face; parented to body so
	// the body's bodyYaw rotation carries it along. Avoids double-rotating.
	let labelMesh = null;
	const labelText = manifest.label ? resolveAnchorLabel(scene, anchor, kind) : null;
	if (labelText && srcBbox) {   // srcBbox null when geometryOwned (no Body clone)
		labelMesh = buildBoxLabel({
			text:      labelText,
			anchorPos: anchor.position,
			frontDir:  new THREE.Vector3(-1, 0, 0),  // canonical -X
			longHalf:  (srcBbox.max.x - srcBbox.min.x) * 0.5,
			topY:      anchor.position.y + (srcBbox.max.y - srcBbox.min.y),
			stage,
		});
	}

	// ─────── Body + Lid yaw rotation around anchor ───────
	// Combined rotation: facingDeg-derived (catalogue canonical → desired
	// box-family facing) + anchor.rotationY (Blender empty rotation per
	// instance). For box-family with facingDeg=180 default and rotY=0:
	// bodyYaw = 0 (catalogue keeps canonical orientation). For shelves
	// with rotY=π/2 (e.g., west-wall): body rotates π/2 so its long axis
	// aligns with the wall direction.
	// Parent the label to the body BEFORE applying bodyYaw, so the rotation CARRIES the label.
	// (ASK-097 C fix: the attach ran AFTER the rotation, and .attach() preserves WORLD transform —
	// so on a facingDeg override the body turned but the label stayed at canonical orientation, i.e.
	// "the label doesn't follow the box on rotation". Attaching first makes the label a child while
	// the body still sits canonical, then the body's rotation propagates to it via the scene graph.)
	if (bodyMesh && labelMesh) bodyMesh.attach(labelMesh);
	const bodyYaw = (Math.PI - facingRad) + (anchor.rotationY || 0);
	const pivotPos = anchor.position.clone();
	if (Math.abs(bodyYaw) > 0.0001) {
		const rotQ = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), bodyYaw);
		for (const mesh of [bodyMesh, lidMesh]) {
			if (!mesh) continue;
			const delta = mesh.position.clone().sub(pivotPos).applyQuaternion(rotQ);
			mesh.position.copy(pivotPos).add(delta);
			mesh.quaternion.premultiply(rotQ);
		}
	}
	// ASK-145 §13.4 — record the path + the move-rotate yaw this anchor was drawn at, so a later
	// hydrate that carries a different yaw can tell the mesh is not there yet (stage.getFixtureYaw).
	stage._noteComposedPose?.(anchor.name, "composer", anchor.rotationY || 0);

	// ─────── Slot layout ───────
	// productType resolution priority: slot.productType (explicit) →
	// affinity[0] (Content's field) → "comic" (legacy default).
	const productType = primarySlotDef?.productType
		|| (Array.isArray(manifest.affinity) ? manifest.affinity[0] : null)
		// A grammar socket already says what it takes; a kind with no manifest slot at all would
		// otherwise fall to the "comic" legacy default and filter a vinyl bin down to nothing.
		|| grammarSocket?.acceptedProductTypes?.[0]
		|| "comic";
	// On the GRAMMAR path the pool is the products assigned to THAT SOCKET, not to the anchor.
	//
	// It made no difference while the only authored grammar socket was the longbox's single LANE.
	// It makes every difference the moment a fixture carries a derived `surface` socket ALONGSIDE its
	// authored ones (ASK-121 item 0 — a display table gets a TOP as well as its FEATURED spot): an
	// anchor-wide pool would route the featured hero and the fan spread onto the table top as well,
	// and the fixture would render its whole stock twice over in one region. `productsForSocket` is
	// the filter the multi-socket path has always used, and it already reads an ABSENT socketId as
	// "no opinion" rather than as a mismatch (WoW §6 — absent and empty are never the same branch),
	// so today's feeds are unchanged to the byte.
	// A grammar socket's type filter is its ACCEPTS — segment-matched against the registry,
	// exactly the set the layout's own admission reads — never the single legacy string. That
	// string falls back to "comic" for a derived root-accepts socket, which filtered a figurine
	// out of the seat GCG's resolver had met for it: the statue was on the feed and the pool
	// handed to the layout never contained it (P2-E2 addendum — "zero meshes drawn").
	const poolForSocket = (sock) => {
		const accepted = (sock?.accepts?.length && stage._productTypes)
			? new Set(typesAccepting(sock.accepts, typeRegistry(stage._productTypes)))
			: null;
		return productsForSocket(products, anchor.name, sock.id, accepted?.size ? accepted : productType);
	};
	// One pool per grammar socket, in socket order; the anchor's pool is their concatenation, so a
	// product assigned to a shelf is counted (and overflow-checked) exactly once.
	const socketPools = grammarSockets.map(poolForSocket);
	// 🔴 ASK-139 F-139af — the MANIFEST lane filters through the registry too. It compared the raw
	// type string to the slot's single `productType`, so a `hardcover-book` line on a book row (slot
	// productType "book") was dropped before any layout saw it — the same raw-id refusal ASK-126 S3.3
	// removed from eligibility. The lane's catalogue socket says what it accepts in tag paths; with
	// none, the legacy string still decides.
	const laneSocket = grammarSocket ? null : ((fixtureCatalogue?.[kind]?.sockets || [])[0] || null);
	const productPool = grammarSocket
		? socketPools.flat()
		: laneSocket?.accepts?.length
			? poolForSocket({ ...laneSocket, id: null })
			: stage._getProductsForAnchor
				? stage._getProductsForAnchor(anchor.name, products, productType)
				: (products || []);

	let slotResult = { slots: [], extras: { slotCount: 0, maxSlots: 0 } };
	if (!geometryOwned && layoutAlgo) {
		const layoutCtx = {
			anchor,
			manifest,
			slotDef:       primarySlotDef,                          // legacy v2 callers; v3 primitives prefer `variant`
			variant:       primaryVariant,                          // canonical variant shape (resolved from v2 or v3)
			// On the grammar path the manifest's authored fill (`count.default` — the longbox's 113)
			// is exactly the number ASK-112 retires: the DOMAIN decides how many fit. A merchant's
			// explicit override still caps, and so does a variant with a FIXED count, because both
			// are somebody choosing a presentation rather than an authored guess at capacity.
			// (Open seam, flagged to Coord: display variants also name a layout ALGORITHM, which a
			// grammar socket ignores. Nothing reconciles the two yet, and nothing needs to today —
			// the only authored socket sits on a fixture whose variants all fill the same lane.)
			countOverride: grammarSocket
				? (typeof primarySlotOverride?.count === "number" ? primarySlotOverride.count
					: typeof primaryVariant?.count?.fixed === "number" ? primaryVariant.count.fixed : null)
				: resolveCountOverride(primarySlotOverride, primaryVariant),
			frontDir,
			productCount:  socketPools[0]?.length ?? productPool.length,
			bodyMesh,
			// ASK-112 S4 additions — ignored by every pre-grammar primitive.
			socket:         grammarSocket,
			laneSocket,                                             // ASK-139 F-139af — the manifest lane's catalogue socket (shelf-row)
			products:       grammarSocket ? socketPools[0] : productPool,   // per-SKU dimensions → per-item packing
			productTypes:   stage._productTypes,                    // boot.productTypes: canonical frame + pose presets
			catalogueFrame: offsetVec ? { offset: offsetVec, pivot: anchor.position, yaw: bodyYaw } : null,
		};
		const runLayout = (name) => {
			const fn = getSlotLayout(name);
			if (!fn) { console.warn(`[composer] ${kind}: unknown slot-layout '${name}'; skipping slot placement`); return null; }
			return fn(layoutCtx);
		};
		let result = runLayout(layoutAlgo);
		// The grammar returns null when it cannot draw the socket it was given (a word it doesn't
		// know, a pose the product type doesn't declare, a degenerate domain). That is a data
		// problem to fix — and until it is, the fixture renders the way it did yesterday rather
		// than losing its shelf. The reason is already on the console from the adapter.
		if (result === null && layoutAlgo === "grammar" && manifestLayoutAlgo) {
			console.warn(`[composer] ${kind} ${anchor.name}: grammar socket not renderable; falling back to '${manifestLayoutAlgo}'`);
			result = runLayout(manifestLayoutAlgo);
		}
		if (result) slotResult = result;
		// …and every FURTHER grammar socket (the shelves under a top), each with its own pool. Their
		// seats join this one composed entry: each slot already names its `socketId`, which is what
		// stamps the seat, so populate and the Studio read them exactly as they read the first. A
		// refusal (fit, footprint) is already named per item by the grammar layer itself.
		for (let k = 1, off = socketPools[0]?.length || 0; layoutAlgo === "grammar" && k < grammarSockets.length; off += socketPools[k++].length) {
			const pool = socketPools[k], more = pool.length ? getSlotLayout("grammar")?.({ ...layoutCtx, socket: grammarSockets[k], products: pool, productCount: pool.length }) : null;
			if (!more) continue;
			if (more.extras?.region) stage._registerSurfaceRegion?.(more.extras.region);
			const x = slotResult.extras || {}, idx = (r) => r.extras?.seatOrder || r.slots.map((_, i) => i);
			slotResult = { ...slotResult, slots: [...slotResult.slots, ...more.slots], extras: { ...x,
				slotCount: (x.slotCount || 0) + more.slots.length, maxSlots: (x.maxSlots || 0) + (more.extras?.maxSlots ?? more.slots.length),
				seatOrder: [...idx(slotResult), ...idx(more).map((i) => i + off)] } };
		}
	}
	// ASK-121: a `surface` socket resolved to a REGION. Register it as scene truth so a pointer over
	// this fixture can be turned into the `{u,v}` the merchant is looking at. Registered here, where
	// the world transform was just applied, rather than re-derived at drag time from a catalogue read
	// — the same reason the domain itself is composed with the body's transform and not the anchor's
	// yaw: two derivations of one frame is how a lane ends up where the box isn't.
	if (slotResult.extras?.region) stage._registerSurfaceRegion?.(slotResult.extras.region);
	const slotCount = slotResult.extras?.slotCount ?? 0;
	const maxSlots  = slotResult.extras?.maxSlots ?? slotCount;

	// `slotCount === 0` has meant "this fixture seated nothing" for as long as every primitive placed
	// its own items. A SOCKET-SEATED primitive (`single-spot`) returns zero slots BY DESIGN and hands
	// the items to the per-socket block below, so reporting it as empty would print "no comic
	// products" over a stand that is about to display one. The primitive says which it is.
	if (slotCount === 0 && !slotResult.extras?.socketSeated) {
		console.log(`[composer] ${kind}: ${anchor.name} no ${productType} products — rendering empty + interactive`);
	}
	// Overflow is published AFTER every seating path has run (see the multi-socket block below), not
	// here. Measured on vanessa's shop 2026-08-26 (ASK-112 eyes-on): the pile table seats its 7
	// products through the multi-socket path, where `slotCount` is 0 by construction — so this test
	// relayed "PILE_TABLE_ANCHOR: 7 surplus" to GCG for a fixture that was seating all 7 correctly.
	// A capacity signal that reports a healthy fixture as overflowing is worse than no signal: it is
	// the input GCG's overflow chain reasons about.
	// WHICH products the slot-layout actually seated, in slot order.
	//
	// Every layout before ASK-121's `scatter` seated a PREFIX of the pool, so "the first slotCount
	// products" was a safe read. A surface region can refuse an individual row on `fit` (D-064 — the
	// mechanism that stops a table receiving a piano) and orders its seats by position rather than by
	// feed order, so the prefix is no longer true there. A layout that knows which rows it seated now
	// SAYS so; one that doesn't keeps the old read, byte-for-byte.
	const seatOrder = Array.isArray(slotResult.extras?.seatOrder) ? slotResult.extras.seatOrder : null;
	const seatedPool = seatOrder
		? seatOrder.map((i) => productPool[i]).filter(Boolean)
		: productPool.slice(0, slotCount);
	let seatedIds = new Set(seatedPool.map((p) => p?.id).filter((id) => id != null));

	// ─────── ComposedEntry skeleton ───────
	const fixtureLike = {
		id: anchor.name,
		name: `${kind.charAt(0).toUpperCase() + kind.slice(1)} ${anchor.index || 1}`,
		templateKey: `${kind}-anchor`,
		transform: {
			position: { x: anchor.position.x, y: anchor.position.y, z: anchor.position.z },
			rotation: { x: 0, y: (anchor.rotationY || 0), z: 0 },
		},
	};
	const composedEntry = {
		fixtureId:        anchor.name,
		fixture:          fixtureLike,
		frontDir,                                          // primitives read this
		worldGroup:       null,
		longboxRoot:      null,
		lidNode:          null,
		lidBaseQuat:      new THREE.Quaternion(),
		lidPivotNode:     null,
		lidPivotBaseQuat: new THREE.Quaternion(),
		lidMeshNode:      null,
		lidMeshBaseY:     0,
		lidHitZone:       null,
		heroExtract:      slotResult.extras?.heroExtract?.clone() || null,
		laneCenter:       slotResult.extras?.laneCenter?.clone() || anchor.position.clone(),
		anchors: {
			laneCenter:     slotResult.extras?.laneCenter || anchor.position.clone(),
			laneSize:       slotResult.extras?.laneSize   || new THREE.Vector3(0.12, 0.08, 0.08),
			slotOne:        slotResult.slots[0]?.position?.clone() || anchor.position.clone(),
			slotStepVector: slotResult.extras?.slotStepVector || new THREE.Vector3(),
			slotQuaternion: slotResult.extras?.slotQuaternion || new THREE.Quaternion(),
			heroExtract:    slotResult.extras?.heroExtract?.clone() || anchor.position.clone(),
			slots:          slotResult.slots,   // per-slot poses → populateComics non-linear placement (DECISION-052)
			approach:       slotResult.extras?.approach || null,   // ASK-139 F-139af — the shelf's front, for a spine to face
		},
		// ASK-112 S4 — what the grammar resolved for this fixture, kept as SCENE TRUTH rather than
		// only as a console line: the probe reads it, and a Studio inspector will want it too.
		// Null for every fixture still rendering from its manifest algorithm.
		grammar:          slotResult.extras?.grammar || null,
		volume:           slotResult.extras?.volume || null,   // ASK-139 F-139an — the bin the volume layout packed (world box)
		panel:            slotResult.extras?.panel || null,    // ASK-147 §5.4 — the wall bay's frame the panel grid packed (world)
		longboxMode:      "anchor",
		longboxErrors:    [],
		longboxWarnings:  [],
		comicEntries:     [],
		interactiveMeshes: [],
		comicsLoaded:     false,
		comicsLoading:    false,
	};

	// ─────── Wire interactions FIRST ───────
	// Some interaction primitives own product rendering (e.g.,
	// shelf-show-all for shelf-row family — creates flat face-out
	// wall-comic meshes per slot). Those primitives populate
	// composedEntry.productsRendered = true. Box-family interactions
	// (open-close / browse-1d / hero-pickup) don't render products
	// directly; populateComics handles their rendering below.
	const interactions = Array.isArray(manifest.interactions) ? manifest.interactions : [];
	const interactionDisposes = [];
	for (const interactionDef of interactions) {
		if (!interactionDef?.kind) continue;
		const wireFn = getInteraction(interactionDef.kind);
		if (!wireFn) {
			console.warn(`[composer] ${kind}: unknown interaction '${interactionDef.kind}'; skipping`);
			continue;
		}
		try {
			// awaited — geometry-owning interactions (friction-spin) are async (GLB load) and
			// must finish (set productsRendered/geometryOwned) before the populate blocks below.
			const dispose = await wireFn({
				anchor,
				manifest,
				interactionDef,
				bodyMesh,
				lidMesh,
				slotResult,
				stage,
				scene,
				products: productPool.slice(0, slotCount),
				productsAll: products,        // DECISION-051a — full list for per-socket fill (friction-spin)
				fixtureCatalogue,             // friction-spin instances the GLB
				token,
				composedEntry,
			});
			if (typeof dispose === "function") interactionDisposes.push(dispose);
		} catch (err) {
			console.error(`[composer] ${kind} ${anchor.name} interaction '${interactionDef.kind}' wire failed:`, err);
		}
	}
	composedEntry._interactionDisposes = interactionDisposes;
	// 🔴 ASK-139 F-139an — STOCK STANDING EDGE-OUT IN AN OPEN VOLUME IS A BROWSE BIN. A lidded box
	// pages its stock through the lid lifecycle (`in-box`); a shelf hands each item down (`shelf`);
	// an open bin of sleeves standing edge-out is flipped through front to back — the record-store
	// gesture the vinyl-bin interaction already implements. The SOCKET says so (domain × pose); no
	// manifest has to, and an interaction a manifest does declare still wins.
	const g0 = slotResult.extras?.grammar;
	if (!composedEntry.populateInteraction && g0?.domain === "volume" && g0?.pose === "edge-out" && !lidMesh) {
		composedEntry.populateInteraction = "flip-bin";
	}
	// 🔴 ASK-147 §5.4 — STOCK FACE-OUT ON A WALL PANEL IS A DISPLAY. The grid has stood each item
	// upright in its cell, cover out; a sleeve joins the vinyl wall display, anything else is seated.
	if (!composedEntry.populateInteraction && slotResult.extras?.panel && g0?.distribution === "grid") {
		composedEntry.populateInteraction = "panel";
	}

	// ─────── Fixture material-library binding (DECISION-053 Slice-2) ───────
	// The fixture-side of shell material application. templateSurfaceMaterials[templateId][kind] is
	// a whole-fixture materialId (pile-table/longbox) or a per-slot object (spinner {frame:...}); the
	// catalogue's materialSlots maps each slot → mesh indices / material-names. Applied to the
	// fixture's frame meshes — geometryOwned (spinner) uses the PRISTINE snapshot friction-spin took
	// before seating products, so numeric slot ids don't shift.
	const materialSlots = fixtureCatalogue?.[kind]?.materialSlots;
	// ASK-085 part-2 — finish-aware binding: byKind finish (FIXTURES dropdown) > static per-kind;
	// omitted kinds keep identity. Shared resolver — the rebind path uses the same one.
	const fxBinding = stage._resolveFixtureBinding
		? stage._resolveFixtureBinding(kind, anchor.name)
		: stage._templateSurfaceMaterialsCached?.[scene?.template?.id]?.[kind];
	const headerIds = materialSlots?.header;
	if (materialSlots) {
		// Fixture frame meshes — geometryOwned (spinner) uses the PRISTINE snapshot friction-spin
		// took before seating products (numeric slot ids mustn't shift).
		const fxMeshes = geometryOwned
			? (composedEntry.fixtureFrameMeshes || collectMeshes(composedEntry.worldGroup))
			: collectMeshes([bodyMesh, lidMesh]);
		const anisotropy = Math.min(8, stage.renderer?.capabilities?.getMaxAnisotropy?.() || 8);
		// (a) Library-material bindings (metal/wood/cardboard) — tiled, box-projected.
		if (fxBinding && stage._materialLibraryCached) {
			try {
				const n = await applyFixtureLibraryMaterials({
					meshes: fxMeshes, materialSlots, binding: fxBinding,
					materialLibrary: stage._materialLibraryCached, renderer: stage.renderer, anisotropy, tier: stage.renderTier,
				});
				if (n && token === stage.currentToken) console.log(`[composer] ${kind} '${anchor.name}': ${n} fixture mesh(es) skinned via material library`);
			} catch (err) {
				console.warn(`[composer] ${kind} fixture material binding failed:`, err?.message || err);
			}
		}
		// ASK-085 — register for live repaint REGARDLESS of a current binding: recomposeTemplate
		// re-resolves (static + finish byKind) fresh, so a FIXTURES-dropdown pick can paint a kind
		// that had no static binding at compose time (same pattern as the header-signage rebinds).
		// _appliedKey = idempotence: rebinds skip when the resolved binding is unchanged (slider
		// drags recompose per input frame — don't rebuild identical materials each tick).
		(stage._fixtureMaterialRebinds ||= []).push({ kind, anchorName: anchor.name, meshes: fxMeshes, materialSlots, _appliedKey: fxBinding ? JSON.stringify(fxBinding) : null });
		// (b) Signage slot (e.g. spinner-rack "header") — dynamic material with LAYERED precedence:
		// per-anchor headerSource/label override > shop signMode + shop brand (name never-blank
		// fallback). Decal on authored UVs, not tiled. Data-gated on a "header" slot. A rebind is
		// registered so a Studio per-anchor knob change hot-swaps the header live (recomposeTemplate).
		// ASK-151 step 4 — a fixture that DECLARES a `text` setting on its header slot owns that slot: its default is the
		// bytes ("COMICS" on the hanging sign) and the merchant's text is the setting's — the shop-name signage is not
		// laid over it (measured on the plane: every placed comics sign read the shop's name, mirrored, not its own word).
		const headerOwnedBySetting = (fixtureCatalogue?.[kind]?.settings || []).some((d) => d?.type === "text" && headerIds && d.target?.materialSlot === "header");
		if (Array.isArray(headerIds) && headerIds.length && !headerOwnedBySetting) {
			try {
				const rebind = { meshes: fxMeshes, ids: headerIds, anchorName: anchor.name, extrasLabel: anchor.extras?.label ?? null };
				const n = await applyHeaderSignage({ stage, scene, ...rebind });
				(stage._fixtureSignageRebinds ||= []).push(rebind);
				if (n && token === stage.currentToken) console.log(`[composer] ${kind} '${anchor.name}': header signage → ${n} face(s)`);
			} catch (err) {
				console.warn(`[composer] ${kind} header signage failed:`, err?.message || err);
			}
		}
	}

	// ─────── Populate products into slots ───────
	// Skipped when an interaction primitive already rendered products
	// (composedEntry.productsRendered = true).
	//
	// ASK-126 S5 gap 2: this guard used to read `productType === "comic"` — the PRODUCT FAMILY
	// deciding whether the system draws at all, which is the exact shape D-063 exists to remove.
	// Measured on the plane: the decor bookshelf's grammar layout planned its seats and NOTHING
	// drew, because its apt[0] is `decor-book`; the pedestal drew the statue only because ITS
	// apt[0] happens to be "comic". `populateComics` already decides per LINE what a seat draws
	// (catalogue mesh · comic template · a refusal BY NAME — P2-E2 ruling 2), so the only honest
	// gate here is "the layout seated something".
	const ctxComic = stage._currentComicAssets || {};
	if (!composedEntry.productsRendered
		&& slotCount > 0
		&& stage.populateComics
	) {
		await stage.populateComics({
			fixture:        fixtureLike,
			anchors:        composedEntry.anchors,
			comicTemplate:  ctxComic.template,
			products:       seatedPool,
			materialSlots:  ctxComic.materialSlots,
			token,
			comicStore:     composedEntry,
			maxSlots,
			interaction:    composedEntry.populateInteraction || "in-box",   // ASK-139 F-139af — a shelf is picked from, not browsed
			browseVerbs:    !lidMesh,   // ASK-154 seam 2 — the pull-out/pick-up pair; a lidded box keeps its shipped riffle
		});
		if (token !== stage.currentToken) return null;
		// A closed BOX shows only its front comic — that is the LID's lifecycle, not a render
		// default, so it keys on the fixture HAVING a lid (the box family), not on the product
		// family or the layout shape. A surface layout (ASK-121) has no closed state, and neither
		// does a lidless shelf: every seated item IS the merchandising — hiding entries 1..n drew
		// one comic on a pedestal the resolver had stocked with five, and would have drawn one
		// book on a shelf dressed with a row of them.
		const surfaceLayout = (composedEntry.anchors.slots || []).some((s) => Number.isFinite(s?.at?.u));
		const hasLid = !!lidMesh;
		if (!surfaceLayout && hasLid) {
			for (let i = 1; i < composedEntry.comicEntries.length; i++) {
				composedEntry.comicEntries[i].root.visible = false;
			}
		}
		composedEntry.comicsLoaded = true;
	}

	// ─────── Multi-socket placement (DECISION-052) ───────
	// Socket-driven fixtures with NO manifest.slots (e.g. pile-table) place each catalogue
	// socket's curated products. NOT via populateComics (it clones the comic TEMPLATE and so
	// applies the box-orientation quaternion to the wrong base → leaning/upright, and wires the
	// longbox "boxed-comic" → wrong selection — walkthrough 2026-06-30 / coord 7fc9112). Instead
	// reuse the VERIFIED socket-path render: _buildSocketComic (qFlat×qYaw) + the pile-comic/
	// wall-comic wiring. The composer already supplied the table geometry (Body clone above).
	// Additive — the single-slot path above (longbox/shelf) is untouched.
	const catSockets = fixtureCatalogue?.[kind]?.sockets;
	if (!composedEntry.productsRendered && slotCount === 0
		&& Array.isArray(catSockets) && catSockets.length && stage._seatTableSocket) {
		const worldSockets = socketsForAnchor(anchor, fixtureCatalogue);   // local TRS → world facts + mapped layout
		for (const socket of worldSockets) {
			if (token !== stage.currentToken) return null;
			const sType = socket.accepts?.[0] || "comic";
			if (sType !== "comic") continue;   // comic-only for now
			const sProducts = productsForSocket(products, anchor.name, socket.socketId, sType);
			if (!sProducts.length) continue;
			const placed = stage._seatTableSocket(socket, sProducts, anchor.name) || 0;   // ASK-087: register ownership per anchor
			for (const p of sProducts.slice(0, placed)) if (p?.id != null) seatedIds.add(p.id);
		}
		composedEntry.comicsLoaded = true;
	}

	// ─────── Overflow, once every seating path has had its turn ───────
	// Surplus = the products curated to this ANCHOR that no path seated, by id — not "everything past
	// slotCount", which counted a multi-socket fixture's whole fill as overflow (see the note above).
	// A geometry-owning interaction reports its own seated ids; if one ever renders products without
	// saying which, we publish NOTHING rather than a number we did not measure.
	for (const id of (composedEntry.seatedProductIds || [])) seatedIds.add(id);
	const countable = !composedEntry.productsRendered || Array.isArray(composedEntry.seatedProductIds);
	if (stage._publishCapacityOverflow && countable) {
		const surplus = productPool.filter((p) => p?.id != null && !seatedIds.has(p.id)).map((p) => p.id);
		if (surplus.length) stage._publishCapacityOverflow(anchor.name, surplus);
	}

	// ─────── Start animations ───────
	const animations = Array.isArray(manifest.animations) ? manifest.animations : [];
	const animationStops = [];
	for (const animationDef of animations) {
		if (!animationDef?.kind) continue;
		const startFn = getAnimation(animationDef.kind);
		if (!startFn) {
			console.warn(`[composer] ${kind}: unknown animation '${animationDef.kind}'; skipping`);
			continue;
		}
		try {
			const stop = startFn({ anchor, manifest, animationDef, bodyMesh, stage });
			if (typeof stop === "function") animationStops.push(stop);
		} catch (err) {
			console.error(`[composer] ${kind} ${anchor.name} animation '${animationDef.kind}' start failed:`, err);
		}
	}
	composedEntry._animationStops = animationStops;

	// ─────── Shadow enrollment (coord df8748f) ───────
	// Composer-instanced fixture geometry wasn't cast-enrolled — box-family Body/Lid clones ship
	// castShadow=false — so the center table / spinner / longbox cast no shadow (only the baked
	// shell did). Set castShadow + receiveShadow on every instanced fixture mesh + its seated
	// products (idempotent for meshes already enrolled in their builders). One pass, perf-negligible.
	for (const root of [bodyMesh, lidMesh, composedEntry.worldGroup]) {
		if (root) root.traverse((n) => { if (n.isMesh) { n.castShadow = true; n.receiveShadow = true; } });
	}

	// ─────── Register with stage state ───────
	composedEntry.bodyMesh = bodyMesh;   // ASK-142 F-142e — the bin the browse pose frames (null when geometry-owned)
	if (stage.boxEntries) stage.boxEntries.set(composedEntry.fixtureId, composedEntry);
	if (composedEntry.lidHitZone && stage.lidMeshes) stage.lidMeshes.push(composedEntry.lidHitZone);

	const g = slotResult.extras?.grammar;
	console.log(`[composer] ${kind} ${anchor.name}: ${slotCount} products in ${slotResult.extras?.laneSize?.x?.toFixed(2) || "?"}m lane (max ${maxSlots})`
		+ (g ? ` | grammar ${g.domain}×${g.distribution}×${g.pose} · domain ${g.domainLength}m · pitch ${g.pitch}m (${g.dimsSource}) · capacity ${g.capacityPacked} packed / ${g.capacityReference} ref / ${g.capacityPublished} published` : ""));
	return composedEntry;
}

// Local label builder — copy of longbox-anchor.js's helper so the composer
// is self-contained. Once Phase 6 retires longbox-anchor.js, this is the
// only copy; future migrations of other label-rendering kinds reuse it.
const BOX_LABEL_TEX_CACHE = new Map();
function buildBoxLabel({ text, anchorPos, frontDir, longHalf, topY, stage }) {
	if (!text) return null;
	const W = 0.20;
	const H = 0.06;
	const key = String(text);
	let tex = BOX_LABEL_TEX_CACHE.get(key);
	if (!tex) {
		const cw = 512;
		const ch = Math.round(cw * H / W);
		const canvas = document.createElement("canvas");
		canvas.width = cw;
		canvas.height = ch;
		const c2d = canvas.getContext("2d");
		c2d.fillStyle = "#f1e7c8";
		c2d.fillRect(0, 0, cw, ch);
		c2d.strokeStyle = "#85714c";
		c2d.lineWidth = 5;
		c2d.strokeRect(3, 3, cw - 6, ch - 6);
		c2d.fillStyle = "#2c2418";
		c2d.font = `bold ${Math.round(ch * 0.58)}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
		c2d.textAlign = "center";
		c2d.textBaseline = "middle";
		c2d.fillText(String(text).toUpperCase(), cw / 2, ch / 2 + 2);
		tex = new THREE.CanvasTexture(canvas);
		tex.colorSpace = THREE.SRGBColorSpace;
		tex.anisotropy = 4;
		tex.needsUpdate = true;
		BOX_LABEL_TEX_CACHE.set(key, tex);
	}
	const mat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.95, metalness: 0 });
	const mesh = new THREE.Mesh(new THREE.PlaneGeometry(W, H), mat);
	const labelPos = anchorPos.clone().addScaledVector(frontDir, longHalf + 0.005);
	labelPos.y = topY - 0.025 - H / 2;
	mesh.position.copy(labelPos);
	mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), frontDir.clone().normalize());
	stage.scene.add(mesh);
	if (stage.shopLights) stage.shopLights.push(mesh);
	return mesh;
}

// Per-kind shell-baked mesh naming map. Shell GLBs author legacy
// procedural geometry under conventions that diverge by family:
//   - Box family (longbox, shortbox): Shell_<Pascal>_Body[_NN], Shell_<Pascal>_Lid[_NN]
//   - Wall-shelf:                     Shell_WallShelf_Board_NN
//   - Book-row:                       Shell_BookShelf_Board_NN + Shell_BookShelf_Bracket_NN_L/R
//   - Vinyl-wall-shelf:               (none in current shells)
// Note book-row's shell prefix is "BookShelf" not "BookRow" — Kapow's
// authoring convention. Future shells could normalize but until then
// this map handles the mismatch.
//
// To hide a per-anchor instance's shell-baked equivalent: try every
// (prefix, subname, suffix-variant) combination and visible=false any
// that match. Suffixed first (`_01`); unsuffixed fallback for shells
// using legacy single-instance naming (e.g. Shell_Longbox_Body without
// _01 for the first longbox).
const SHELL_PREFIXES_BY_KIND = {
	"longbox":          { pascal: "Longbox",         subnames: ["Body", "Lid"] },
	"shortbox":         { pascal: "Shortbox",        subnames: ["Body", "Lid"] },
	"wall-shelf":       { pascal: "WallShelf",       subnames: ["Board"] },
	"book-row":         { pascal: "BookShelf",       subnames: ["Board", "Bracket"] },
	"vinyl-wall-shelf": { pascal: "VinylWallShelf",  subnames: ["Board"] },
};

function hideShellBakedForKind(shellRoot, kind, anchorIndex) {
	const map = SHELL_PREFIXES_BY_KIND[kind];
	if (!map) return;
	const indexSuffix = String(anchorIndex).padStart(2, "0");
	for (const sub of map.subnames) {
		const base = `Shell_${map.pascal}_${sub}`;
		// Try suffixed name, plus _L / _R variants (book-row brackets),
		// plus unsuffixed legacy single-instance name.
		const candidates = [
			`${base}_${indexSuffix}`,
			`${base}_${indexSuffix}_L`,
			`${base}_${indexSuffix}_R`,
			base, // legacy unsuffixed (e.g. Shell_Longbox_Body for the first anchor)
		];
		for (const name of candidates) {
			const node = shellRoot.getObjectByName(name);
			if (node) node.visible = false;
		}
	}
}

// Resolve the active variant for a slot, normalising v2 and v3
// manifest shapes to a single canonical variant object the slot-layout
// primitive can consume.
//
// v3 input: slotDef has `displayVariants: [{ id, layoutAlgorithm, count:
//           {min,max,default}|{fixed}, params }, ...]` + `defaultVariant: id`
// v2 input: slotDef carries layoutAlgorithm + count + spacingMultiplier
//           + marginMultiplier + extractDistance + extractHeightFactor
//           directly on the slot def (single-variant equivalent)
//
// Output (canonical): { id, layoutAlgorithm, count, params } where
//   - `count` is the resolved envelope object (or scalar for legacy)
//   - `params` is the algorithm-specific parameter bag
//
// Merchant override: when `slotOverride?.displayVariant` matches a
// variant id in v3, that variant is chosen; otherwise `defaultVariant`.
function resolveActiveVariant(slotDef, slotOverride) {
	if (!slotDef) return null;

	// v3 path: slot has displayVariants[]
	if (Array.isArray(slotDef.displayVariants) && slotDef.displayVariants.length > 0) {
		const overrideId = slotOverride?.displayVariant;
		const defaultId  = slotDef.defaultVariant;
		const variants = slotDef.displayVariants;
		const targetId = overrideId || defaultId;
		const v = (targetId ? variants.find((x) => x.id === targetId) : null) || variants[0];
		return {
			id:              v.id,
			layoutAlgorithm: v.layoutAlgorithm,
			count:           v.count,
			params:          v.params || {},
		};
	}

	// v2 path: synthesize a single implicit variant from the slot def
	if (slotDef.layoutAlgorithm) {
		const count = typeof slotDef.count === "number"
			? { default: slotDef.count, max: slotDef.count, min: 0 }
			: null;
		return {
			id:              "v2-implicit",
			layoutAlgorithm: slotDef.layoutAlgorithm,
			count,
			params: {
				spacingMultiplier:   slotDef.spacingMultiplier,
				marginMultiplier:    slotDef.marginMultiplier,
				extractDistance:     slotDef.extractDistance,
				extractHeightFactor: slotDef.extractHeightFactor,
			},
		};
	}

	return null;
}

// Resolve the count to use for slot generation given the active variant
// + an optional merchant override. Returns a numeric count or null when
// the variant has no count info (caller should fall through to
// algorithm-derived count).
//
//   - { fixed: K }            → K (merchant cannot override)
//   - { min, max, default }   → merchant.count clamped to [min, max], else default
//   - legacy scalar (v2)      → that number
function resolveCountOverride(slotOverride, variant) {
	if (!variant?.count) return null;
	const c = variant.count;
	if (typeof c.fixed === "number") return c.fixed;
	const overrideCount = typeof slotOverride?.count === "number" ? slotOverride.count : null;
	if (overrideCount !== null) {
		const clamped = Math.max(c.min ?? 0, Math.min(c.max ?? overrideCount, overrideCount));
		return clamped;
	}
	if (typeof c.default === "number") return c.default;
	return null;
}

// Quiet unused import warning — boxedComicReferenceMeters is reserved for
// future composer-side product-dimension reads (today's slot-layout
// primitives import directly).
void boxedComicReferenceMeters;
